import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { usePayment } from "@/hooks/usePayment";
import * as shared from "@/styles/shared";
import { groupLogEntries } from "@platko/shared/utils";
import { PaymentFlowStatus } from "@shared/types/payment";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as liveLogsStyles from "./LiveLogs.styles";
import { LogGroupSection } from "./LogGroupSection";
import * as processingStyles from "./PaymentProcessing.styles";

const PaymentProcessing = () => {
  const payment = usePayment();

  const { t } = useTranslation("payment");
  const { t: tBackend } = useTranslation("backendLogs");

  const [showTwoFa, setShowTwoFa] = useState(false);
  const prevPromptId = useRef<string | null>(null);

  const groups = useMemo(() => groupLogEntries(payment.logEntries, payment.payResults), [payment.logEntries, payment.payResults]);

  useEffect(() => {
    if (payment.logPanelRef.current) payment.logPanelRef.current.scrollTop = payment.logPanelRef.current.scrollHeight;
  }, [payment.logEntries]);

  useEffect(() => {
    if (payment.twoFaPrompt && payment.twoFaPrompt.requestId !== prevPromptId.current) {
      prevPromptId.current = payment.twoFaPrompt.requestId;
      setShowTwoFa(true);
    }
    if (!payment.twoFaPrompt) {
      setShowTwoFa(false);
      prevPromptId.current = null;
    }
  }, [payment.twoFaPrompt]);

  const isComplete = [PaymentFlowStatus.DONE, PaymentFlowStatus.ERROR].includes(payment.paymentStatus);
  const isError = payment.paymentStatus === PaymentFlowStatus.ERROR;

  return (
    <Card variant="glass" className={processingStyles.processingCard}>
      <CardHeader className={processingStyles.processingHeader}>
        <div className={shared.rowGap2}>
          {!isComplete && <span className={processingStyles.processingPulse} aria-hidden="true" />}
          {!isComplete && <Loader2 className="h-3 w-3 animate-spin" />}
          {isComplete && !isError && t("paymentComplete")}
          {isComplete && isError && (
            <span className={processingStyles.errorText}>{payment.payError ? tBackend(payment.payError) : t("paymentFailed")}</span>
          )}
          {!isComplete && t("progress", { count: payment.processedCount, total: payment.selectedCount })}
        </div>
        {!isComplete && (
          <button onClick={payment.handleCancelPayment} className={processingStyles.processingCancel}>
            {t("cancelPayment")}
          </button>
        )}
      </CardHeader>
      <CardContent className={processingStyles.logContent}>
        <div ref={payment.logPanelRef} className={liveLogsStyles.logPanel}>
          {groups.map((group, gi) => (
            <LogGroupSection key={gi} group={group} />
          ))}
          {showTwoFa && payment.twoFaPrompt && (
            <div className={processingStyles.twoFaContainer}>
              <div className={processingStyles.twoFaHeader}>
                <span className={processingStyles.twoFaTitle}>{t("twoFaTitle")}</span>
              </div>
              <div className={processingStyles.twoFaInputGroup}>
                <input
                  type="text"
                  value={payment.twoFaCode}
                  onChange={(e) => payment.patch({ twoFaCode: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && payment.handleSubmit2Fa()}
                  placeholder={t("twoFaPlaceholder")}
                  className={processingStyles.twoFaInput}
                  autoFocus
                />
                <button onClick={payment.handleSubmit2Fa} className={processingStyles.twoFaSubmit}>
                  {t("twoFaSubmit")}
                </button>
                <button onClick={payment.handleCancel2Fa} className={processingStyles.twoFaCancel}>
                  {t("twoFaCancel")}
                </button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentProcessing;
